import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { EMPTY_FILTERS, isFiltered, readFilters, toApiQuery, writeFilters, type ListApiQuery, type ListFilterState } from './listFilters';

const MANAGED = ['status', 'client', 'warehouse', 'from', 'to', 'sort'];

/**
 * List filters kept in the address bar (parameters this hook does not own - e.g. a dashboard deep link's `openOnly` -
 * are left untouched). `onChange` runs after every change (pages use it to go back to the first page).
 */
export function useListFilters(onChange?: () => void): {
  readonly filters: ListFilterState;
  readonly query: ListApiQuery;
  readonly key: string;
  readonly filtered: boolean;
  readonly set: (patch: Partial<ListFilterState>) => void;
  readonly reset: () => void;
} {
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => readFilters(params), [params]);
  const query = useMemo(() => toApiQuery(filters), [filters]);
  const key = useMemo(() => JSON.stringify(query), [query]);
  const apply = useCallback(
    (next: ListFilterState) => {
      const out = new URLSearchParams();
      for (const [k, v] of params) if (!MANAGED.includes(k)) out.append(k, v);
      for (const [k, v] of writeFilters(next)) out.set(k, v);
      setParams(out, { replace: true });
      onChange?.();
    },
    [params, setParams, onChange],
  );
  const set = useCallback((patch: Partial<ListFilterState>) => apply({ ...filters, ...patch }), [apply, filters]);
  const reset = useCallback(() => apply(EMPTY_FILTERS), [apply]);
  return { filters, query, key, filtered: isFiltered(filters), set, reset };
}
